import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import { createColumnSettingsService } from '@/services/ui/columnSettingsService'
import type {
  ColumnSettings,
  ViewMode,
  ColumnConfig,
} from '@farmfree/common/types/order/columnKeys'
import {
  getVisibleColumns,
  getTogglableColumns,
  getDefaultColumnsForMode,
  DEFAULT_COLUMN_SETTINGS,
} from '@farmfree/common/types/order/columnKeys'
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth/useAuthStore'

// 데이터 무결성 보장: Store 레벨에서도 누락된 컬럼 자동 추가
function mergeColumnsWithDefaults(saved: ColumnConfig[], defaults: ColumnConfig[]): ColumnConfig[] {
  const merged = [...saved]

  // 기본값에는 있지만 저장된 값에 없는 컬럼 추가
  for (const defaultCol of defaults) {
    if (!merged.find((col) => col.key === defaultCol.key)) {
      merged.push({ ...defaultCol })
      console.log(`✨ [ColumnStore] 새 컬럼 추가: ${defaultCol.key} (${defaultCol.label})`)
    }
  }

  return merged
}

// 지연 초기화를 위한 서비스 참조
let columnSettingsService: ReturnType<typeof createColumnSettingsService> | null = null
const getService = () => {
  if (!columnSettingsService) {
    columnSettingsService = createColumnSettingsService()
  }
  return columnSettingsService
}

// 팩토리 패턴으로 컬럼 설정 Store 생성
const baseStore = createIndexedDBStore<ColumnSettings>({
  storeId: 'columnSettings',
  tableName: 'columnSettings',
  versionField: 'columnSettingsVersion', // 나중에 versionWatchers에 추가 가능
  service: {
    getModified: (companyId: string, since: number) => getService().getModified(companyId, since),
    getDeleted: (companyId: string) => getService().getDeleted(companyId),
    saveItem: (companyId: string, item: ColumnSettings) => getService().saveItem(companyId, item),
  },
  options: {
    debug: true,
  },
})

// Store에 컬럼 관련 로직 추가
export const useColumnSettingsStore = () => {
  const store = baseStore()
  const authStore = useAuthStore()
  const currentCompanyId = computed(() => authStore.currentCompany?.id || '')

  // 현재 설정 (단일 항목이므로 첫 번째 요소)
  const settings = computed(() => store.items[0] || null)

  // 모드별 컬럼들 (누락된 컬럼 자동 추가)
  const viewColumns = computed(() => {
    const saved = settings.value?.view.columns || []
    return mergeColumnsWithDefaults(saved, DEFAULT_COLUMN_SETTINGS.view.columns)
  })

  const paymentColumns = computed(() => {
    const saved = settings.value?.payment.columns || []
    return mergeColumnsWithDefaults(saved, DEFAULT_COLUMN_SETTINGS.payment.columns)
  })

  const trackingColumns = computed(() => {
    const saved = settings.value?.tracking.columns || []
    return mergeColumnsWithDefaults(saved, DEFAULT_COLUMN_SETTINGS.tracking.columns)
  })

  // 모드별 표시 가능한 컬럼들
  const getColumnsForMode = (mode: ViewMode | 'tracking'): ColumnConfig[] => {
    if (mode === 'view') return viewColumns.value
    if (mode === 'payment') return paymentColumns.value
    return trackingColumns.value
  }

  const getVisibleColumnsForMode = (mode: ViewMode | 'tracking'): ColumnConfig[] => {
    return getVisibleColumns(getColumnsForMode(mode))
  }

  const getTogglableColumnsForMode = (mode: ViewMode | 'tracking'): ColumnConfig[] => {
    return getTogglableColumns(getColumnsForMode(mode))
  }

  // 컬럼 표시/숨김 확인
  const isColumnVisible = (mode: ViewMode | 'tracking', columnKey: string): boolean => {
    const columns = getColumnsForMode(mode)
    const column = columns.find((col) => col.key === columnKey)
    return column?.visible ?? false
  }

  // 컬럼 표시/숨김 토글
  const toggleColumnVisibility = async (
    mode: ViewMode | 'tracking',
    columnKey: string
  ): Promise<void> => {
    if (!settings.value) return

    const columns = getColumnsForMode(mode)
    const columnIndex = columns.findIndex((col) => col.key === columnKey)

    if (columnIndex === -1) return

    // 로컬 상태 업데이트
    columns[columnIndex].visible = !columns[columnIndex].visible

    // 서버에 저장 - 팩토리 패턴의 saveItem 사용
    if (settings.value && currentCompanyId.value) {
      await store.saveItem(currentCompanyId.value, settings.value)
    }
  }

  // 특정 모드의 컬럼 설정만 업데이트
  const updateModeColumns = async (
    mode: ViewMode | 'tracking',
    columns: ColumnConfig[]
  ): Promise<void> => {
    if (!settings.value) return

    // 모드별 업데이트
    if (mode === 'view') {
      settings.value.view.columns = columns
    } else if (mode === 'payment') {
      settings.value.payment.columns = columns
    } else if (mode === 'tracking') {
      settings.value.tracking.columns = columns
    }

    // 서버에 저장 - 팩토리 패턴의 saveItem 사용
    if (settings.value && currentCompanyId.value) {
      await store.saveItem(currentCompanyId.value, settings.value)
    }
  }

  // 주문 카테고리 변경에 따른 컬럼 조정
  const updateColumnsForCategory = (
    mode: ViewMode | 'tracking',
    category: 'general' | 'commerce' | 'entrust' | 'partner' | 'courier'
  ): void => {
    // ⚠️ 주의: 사용자가 저장한 컬럼 설정을 유지합니다.
    // 카테고리 변경 시에도 기본값으로 덮어쓰지 않습니다.
    //
    // 기존 로직: 카테고리마다 다른 컬럼을 보여주려는 의도였으나,
    // 사용자 설정을 무시하고 기본값을 강제 적용하는 문제가 있었습니다.
    //
    // 해결: 현재는 사용자 설정을 그대로 유지하며,
    // 필요 시 추후 카테고리별 컬럼 추천 기능으로 개선할 수 있습니다.

    console.log(`📋 [ColumnSettings] 카테고리 변경 (${mode}, ${category}) - 사용자 설정 유지`)

    // 현재는 아무것도 하지 않음 - 사용자 설정 유지
    // 향후 카테고리별 컬럼 추천이 필요하면 여기에 구현
  }

  return {
    // 팩토리 패턴 기본 기능
    ...store,

    // 컬럼 관련 computed
    settings,
    viewColumns,
    paymentColumns,
    trackingColumns,

    // 컬럼 관련 함수들
    getColumnsForMode,
    getVisibleColumnsForMode,
    getTogglableColumnsForMode,
    isColumnVisible,
    toggleColumnVisibility,
    updateModeColumns,
    updateColumnsForCategory,

    // 누락된 메서드들 (기존 호환성)
    reorderColumns: updateModeColumns, // 임시 별칭
    resetToDefault: async (mode?: ViewMode | 'tracking') => {
      if (!settings.value) return

      // 현재 모드가 지정되지 않으면 모든 모드 초기화
      if (!mode) {
        // 모든 모드를 기본값으로 초기화
        settings.value.view.columns = [...getDefaultColumnsForMode('view')]
        settings.value.payment.columns = [...getDefaultColumnsForMode('payment')]
        settings.value.tracking.columns = [...getDefaultColumnsForMode('tracking')]
        console.log('🔄 [ColumnSettings] 모든 모드를 기본값으로 초기화')
      } else {
        // 특정 모드만 기본값으로 초기화
        const defaultColumns = getDefaultColumnsForMode(mode)

        if (mode === 'view') {
          settings.value.view.columns = [...defaultColumns]
        } else if (mode === 'payment') {
          settings.value.payment.columns = [...defaultColumns]
        } else if (mode === 'tracking') {
          settings.value.tracking.columns = [...defaultColumns]
        }

        console.log(`🔄 [ColumnSettings] ${mode} 모드를 기본값으로 초기화`)
      }

      // 서버에 저장
      if (currentCompanyId.value) {
        await store.saveItem(currentCompanyId.value, settings.value)
        console.log('✅ [ColumnSettings] 기본값 복원 완료 및 저장')
      }
    },
    clearError: () => {
      // 오류 클리어 로직
      store.error = null
    },

    // 팩토리 패턴 메서드 노출
    initializeCache: store.initializeCache,

    // 별칭 (기존 호환성)
    isLoading: store.loading,
    isLoaded: computed(() => store.items.length > 0),
    loadSettings: (companyId: string) => store.initializeCache(companyId),
  }
}
